import React, { Component, useEffect, useRef, useState } from 'react';
import { bool, func, object, string } from 'prop-types';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { FormattedMessage, intlShape, injectIntl, useIntl } from '../../../util/reactIntl';
import { formatMoney, splitPriceCents } from '../../../util/currency';
import { types as sdkTypes } from '../../../util/sdkLoader';
import { isMainSearchTypeKeywords } from '../../../util/search';
import { suggestCategories } from '../../../util/categorySuggestions';
import { getCategoryUrl } from '../../../util/categoryLandingHelpers';
import { createSlug } from '../../../util/urlHelpers';
import { SEARCH_SUGGESTIONS_MIN_LENGTH, suggestListings } from '../../../ducks/browse.duck';

import { Form, Icons, LocationAutocompleteInput, NamedLink } from '../../../components';

import css from './TopbarSearchForm.module.css';

const { Money } = sdkTypes;

const identity = v => v;

// Bold the matched substring in a suggestion name (ledger "Type-ahead polish")
const BoldMatch = ({ name, matchIndex, termLength }) => {
  if (matchIndex < 0) return <>{name}</>;
  return (
    <>
      {name.slice(0, matchIndex)}
      <strong>{name.slice(matchIndex, matchIndex + termLength)}</strong>
      {name.slice(matchIndex + termLength)}
    </>
  );
};

// Prototype price treatment: the price in its own currency, with raised small cents
const SuggestionPrice = ({ price }) => {
  const intl = useIntl();
  if (!price || price.amount == null || !price.currency) return null;
  const { whole, cents } = splitPriceCents(
    formatMoney(intl, new Money(price.amount, price.currency))
  );
  return (
    <span className={css.suggestionPrice}>
      {whole}
      {cents ? <span className={css.suggestionCents}>{cents}</span> : null}
    </span>
  );
};

/**
 * Takeover type-ahead card (ledger §4, measured from the prototype's Home
 * search state): LISTINGS section (first photo + title + price) above
 * CATEGORIES (diagonal arrow + name + green ancestor path) that navigate to
 * the SEO'd category landing pages, then a green "Search all listings" footer
 * that submits the keyword search as usual. Matched substrings are bolded
 * (spec-only item, not simulated in the prototype).
 */
const SearchSuggestions = props => {
  const { term, onSearchAll, onPick } = props;
  const trimmed = (term || '').trim();
  const suggestions = suggestCategories(term);
  // Listings suggested for the words typed (browse.duck suggestListings)
  const listingItems = useSelector(state => state.browse.searchSuggestions);
  if (trimmed.length < SEARCH_SUGGESTIONS_MIN_LENGTH) return null;

  return (
    <div className={css.suggestions}>
      {listingItems.length > 0 ? (
        <>
          <div className={css.sectionHead}>
            <FormattedMessage id="TopbarSearchForm.listingsSection" />
          </div>
          {listingItems.map(item => (
            <NamedLink
              key={item.id}
              name="ListingPage"
              params={{ id: item.id, slug: createSlug(item.title || '') }}
              className={css.listingRow}
              onClick={onPick}
            >
              {item.imageUrl ? (
                <img className={css.listingThumb} src={item.imageUrl} alt="" />
              ) : (
                <span className={css.listingThumb} />
              )}
              <span className={css.listingTitle}>
                <BoldMatch
                  name={item.title || ''}
                  matchIndex={(item.title || '').toLowerCase().indexOf(trimmed.toLowerCase())}
                  termLength={trimmed.length}
                />
              </span>
              <SuggestionPrice price={item.price} />
            </NamedLink>
          ))}
        </>
      ) : null}

      {suggestions.length > 0 ? (
        <>
          <div className={css.sectionHead}>
            <FormattedMessage id="TopbarSearchForm.categoriesSection" />
          </div>
          {suggestions.map(({ category, path, matchIndex }) => (
            <Link
              key={category.id}
              to={getCategoryUrl(category.id)}
              className={css.suggestionRow}
              onClick={onPick}
            >
              <span className={css.suggestionArrow}>
                <Icons name="suggestionArrow" />
              </span>
              <span className={css.suggestionText}>
                <span className={css.suggestionName}>
                  <BoldMatch
                    name={category.name}
                    matchIndex={matchIndex}
                    termLength={trimmed.length}
                  />
                </span>
                {path.length > 0 ? (
                  <span className={css.suggestionPath}>{path.join(' › ')}</span>
                ) : null}
              </span>
            </Link>
          ))}
        </>
      ) : null}

      {/* Mousedown keeps the focus in the search box, so the card stays open for the click */}
      <button
        type="button"
        className={css.searchAllRow}
        onMouseDown={e => e.preventDefault()}
        onClick={onSearchAll}
      >
        <FormattedMessage id="TopbarSearchForm.searchAllListings" values={{ term: trimmed }} />
      </button>
    </div>
  );
};

const KeywordSearchField = props => {
  const {
    keywordSearchWrapperClasses,
    iconClass,
    intl,
    isMobile,
    takeover,
    inputRef,
    autoFocus,
    onSubmitSearch,
  } = props;
  const dispatch = useDispatch();
  const [isFocused, setIsFocused] = useState(false);
  // The blur close is deferred (so a click on a suggestion lands first);
  // clear it on unmount so it can't set state on an unmounted component.
  const blurTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(blurTimerRef.current), []);
  const inputClass = takeover ? css.takeoverInput : isMobile ? css.mobileInput : css.desktopInput;

  // The card closes once the focus leaves the search box and its suggestions (Tab moves
  // through them). Delayed, so a click on a suggestion lands first.
  // NOTE: suggestion rows close the card on CLICK, not mousedown — closing on mousedown
  // unmounted the link before mouseup, so no click ever fired (no navigation).
  const handleBlur = e => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    clearTimeout(blurTimerRef.current);
    blurTimerRef.current = setTimeout(() => setIsFocused(false), 120);
  };
  const handleFocus = () => clearTimeout(blurTimerRef.current);

  return (
    <div
      className={takeover ? css.takeoverWrapper : keywordSearchWrapperClasses}
      onBlur={handleBlur}
      onFocus={handleFocus}
    >
      <Field
        name="keywords"
        render={({ input, meta }) => {
          return (
            <>
              <input
                className={inputClass}
                {...input}
                id={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
                data-testid={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
                ref={inputRef}
                type="text"
                placeholder={intl.formatMessage({
                  id: takeover
                    ? 'TopbarSearchForm.takeoverPlaceholder'
                    : 'TopbarSearchForm.placeholder',
                })}
                autoComplete="off"
                autoFocus={autoFocus}
                onChange={e => {
                  input.onChange(e);
                  // Typing opens the card again after Escape closed it
                  setIsFocused(true);
                  dispatch(suggestListings(e.target.value));
                }}
                onFocus={e => {
                  input.onFocus(e);
                  setIsFocused(true);
                  dispatch(suggestListings(input.value));
                }}
                onKeyDown={e => {
                  if (e.key === 'Escape' && isFocused && !takeover) {
                    setIsFocused(false);
                  }
                }}
              />
              {isFocused ? (
                <SearchSuggestions
                  term={input.value}
                  onSearchAll={() => onSubmitSearch({ keywords: input.value })}
                  onPick={() => setIsFocused(false)}
                />
              ) : null}
            </>
          );
        }}
      />
      {/* Takeover carries its own leading magnifier in the bar; Enter submits */}
      {takeover ? null : (
        <button className={css.searchSubmit}>
          <div className={iconClass}>
            <Icons name="search" />
          </div>
        </button>
      )}
    </div>
  );
};

const LocationSearchField = props => {
  const { desktopInputRootClass, intl, isMobile, inputRef, onLocationChange } = props;
  return (
    <Field
      name="location"
      format={identity}
      render={({ input, meta }) => {
        const { onChange, ...restInput } = input;

        // Merge the standard onChange function with custom behaviur. A better solution would
        // be to use the FormSpy component from Final Form and pass this.onChange to the
        // onChange prop but that breaks due to insufficient subscription handling.
        // See: https://github.com/final-form/react-final-form/issues/159
        const searchOnChange = value => {
          onChange(value);
          onLocationChange(value);
        };

        return (
          <LocationAutocompleteInput
            className={isMobile ? css.mobileInputRoot : desktopInputRootClass}
            iconClassName={isMobile ? css.mobileIcon : css.desktopIcon}
            inputClassName={isMobile ? css.mobileInput : css.desktopInput}
            predictionsClassName={isMobile ? css.mobilePredictions : css.desktopPredictions}
            predictionsAttributionClassName={isMobile ? css.mobilePredictionsAttribution : null}
            placeholder={intl.formatMessage({ id: 'TopbarSearchForm.placeholder' })}
            closeOnBlur={!isMobile}
            inputRef={inputRef}
            input={{ ...restInput, onChange: searchOnChange }}
            meta={meta}
          />
        );
      }}
    />
  );
};

class TopbarSearchFormComponent extends Component {
  constructor(props) {
    super(props);
    // onChange is used for location search
    this.onChange = this.onChange.bind(this);
    // onSubmit is used for keywords search
    this.onSubmit = this.onSubmit.bind(this);

    // Callback ref
    this.searchInput = null;
    this.setSearchInputRef = element => {
      this.searchInput = element;
    };
  }

  onChange(location) {
    const { appConfig, onSubmit } = this.props;
    if (!isMainSearchTypeKeywords(appConfig) && location.selectedPlace) {
      // Note that we use `onSubmit` instead of the conventional
      // `handleSubmit` prop for submitting. We want to autosubmit
      // when a place is selected, and don't require any extra
      // validations for the form.
      onSubmit({ location });
      // blur search input to hide software keyboard
      this.searchInput?.blur();
    }
  }

  onSubmit(values) {
    const { appConfig, onSubmit } = this.props;
    if (isMainSearchTypeKeywords(appConfig)) {
      onSubmit({ keywords: values.keywords });
      // blur search input to hide software keyboard
      this.searchInput?.blur();
    }
  }

  render() {
    const { onSubmit, appConfig, ...restOfProps } = this.props;
    const isKeywordsSearch = isMainSearchTypeKeywords(appConfig);

    const submit = isKeywordsSearch ? this.onSubmit : onSubmit;
    return (
      <FinalForm
        {...restOfProps}
        onSubmit={submit}
        render={formRenderProps => {
          const {
            rootClassName,
            className,
            desktopInputRoot,
            intl,
            isMobile,
            takeover,
            handleSubmit,
          } = formRenderProps;
          const classes = classNames(rootClassName, className);
          const desktopInputRootClass = desktopInputRoot || css.desktopInputRoot;

          // Location search: allow form submit only when the place has changed
          const preventFormSubmit = e => e.preventDefault();
          const submitFormFn = isKeywordsSearch ? handleSubmit : preventFormSubmit;

          const keywordSearchWrapperClasses = classNames(
            css.keywordSearchWrapper,
            isMobile ? css.mobileInputRoot : desktopInputRootClass
          );

          return (
            <Form className={classes} onSubmit={submitFormFn} enforcePagePreloadFor="SearchPage">
              {isKeywordsSearch ? (
                <KeywordSearchField
                  keywordSearchWrapperClasses={keywordSearchWrapperClasses}
                  iconClass={classNames(isMobile ? css.mobileIcon : css.desktopIcon || css.icon)}
                  intl={intl}
                  isMobile={isMobile}
                  takeover={takeover}
                  inputRef={this.setSearchInputRef}
                  autoFocus={this.props.autoFocus}
                  onSubmitSearch={this.onSubmit}
                />
              ) : (
                <LocationSearchField
                  desktopInputRootClass={desktopInputRootClass}
                  intl={intl}
                  isMobile={isMobile}
                  inputRef={this.setSearchInputRef}
                  onLocationChange={this.onChange}
                />
              )}
            </Form>
          );
        }}
      />
    );
  }
}

TopbarSearchFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  desktopInputRoot: null,
  isMobile: false,
  takeover: false,
};

TopbarSearchFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  desktopInputRoot: string,
  onSubmit: func.isRequired,
  isMobile: bool,
  takeover: bool,
  appConfig: object.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const TopbarSearchForm = injectIntl(TopbarSearchFormComponent);

export default TopbarSearchForm;
